import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { useAuth } from "./AuthContext"
import { socketsConfigured, socketsUrl } from "../config"
import { acquireSharedSocket, forceDisconnectSharedSocket, releaseSharedSocket } from "../realtime/sharedSocket"

const SocketContext = createContext(null)

/**
 * The tab's one socket, and whether it is currently connected.
 *
 * `connected` is tracked as state rather than read from `socket.connected` at
 * render time, because a plain property change does not re-render anything — the
 * UI would show "connecting…" forever, or show connected after the link dropped.
 */
export function SocketProvider({ children }) {
  const { token, user } = useAuth()
  const [socket, setSocket] = useState(null)
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    // No engine configured: the deployment has not got one yet. Opening a
    // connection to nowhere would retry thirty times and achieve the same
    // nothing, so realtime is simply absent and the rest of the app is unaffected.
    if (!socketsConfigured || !token || !user?.id) {
      forceDisconnectSharedSocket()
      setSocket(null)
      setConnected(false)
      return undefined
    }

    const instance = acquireSharedSocket(socketsUrl, token, user.id)
    setSocket(instance)
    setConnected(instance.connected)

    const onConnect = () => setConnected(true)
    const onDisconnect = () => setConnected(false)

    instance.on("connect", onConnect)
    instance.on("disconnect", onDisconnect)

    return () => {
      instance.off("connect", onConnect)
      instance.off("disconnect", onDisconnect)
      // Release rather than disconnect: another consumer may still hold it, and
      // the shared socket's grace period absorbs a remount.
      releaseSharedSocket()
    }
  }, [token, user?.id])

  const value = useMemo(() => ({ socket, connected }), [socket, connected])
  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export function useSocket() {
  return useContext(SocketContext) ?? { socket: null, connected: false }
}

/**
 * Subscribe to a socket event for the lifetime of a component.
 *
 * The handler is stored in a ref inside the effect's closure via the dependency
 * on `socket` only — pass a `useCallback`'d handler, or an inline arrow will
 * re-subscribe on every render.
 */
export function useSocketEvent(event, handler) {
  const { socket } = useSocket()
  useEffect(() => {
    if (!socket || !handler) return undefined
    socket.on(event, handler)
    return () => socket.off(event, handler)
  }, [socket, event, handler])
}
